import { useEffect, useMemo, useState } from 'react'
import {
  listStudentLandingPages,
  deleteStudentLandingPage,
} from '../api/studentLandingApi'
import { loadMasterOptions } from '../../students/api/mastersApi'
import { appConfirm } from '../../../../components/AppConfirmModal'
import { appToast } from '../../../../components/AppToast'

export function useStudentLandingListScreen() {
  const [searchField, setSearchField] = useState('Page Name')
  const [companyFilter, setCompanyFilter] = useState('All')
  const [branchFilter, setBranchFilter] = useState('All')
  const [searchText, setSearchText] = useState('')
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState('')
  const [items, setItems] = useState([])
  const [masterOptions, setMasterOptions] = useState({ institutes: [], branches: [] })

  useEffect(() => {
    loadMasterOptions(['institutes', 'branches'])
      .then(setMasterOptions)
      .catch(() => setMasterOptions({ institutes: [], branches: [] }))
  }, [])

  const queryParams = useMemo(
    () => ({
      searchField,
      company: companyFilter,
      branch: branchFilter,
      searchText,
    }),
    [searchField, companyFilter, branchFilter, searchText],
  )

  const loadPages = async () => {
    setIsLoading(true)
    setError('')

    try {
      const allPages = await listStudentLandingPages(queryParams)
      setItems(allPages)
    } catch {
      setError('Failed to load landing pages.')
      setItems([])
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    loadPages()
  }, [queryParams])

  const resetFilters = () => {
    setSearchField('Page Name')
    setCompanyFilter('All')
    setBranchFilter('All')
    setSearchText('')
  }

  const handleDelete = async (landingId) => {
    const confirmed = await appConfirm({
      title: 'Delete Landing Page',
      message: 'Are you sure you want to permanently delete this student landing page entry? This action cannot be undone.',
      confirmText: 'Delete',
      variant: 'danger',
    })
    if (!confirmed) return

    try {
      await deleteStudentLandingPage(landingId)
      appToast.success('Landing page entry deleted successfully.')
      await loadPages()
    } catch {
      appToast.error('Failed to delete the landing page.')
      setError('Failed to delete the landing page.')
    }
  }

  return {
    state: {
      searchField,
      companyFilter,
      branchFilter,
      searchText,
      isLoading,
      error,
      rows: items,
      totalCount: items.length,
      options: {
        companyOptions: ['All', ...(masterOptions.institutes || [])],
        branchOptions: ['All', ...(masterOptions.branches || [])],
      },
    },
    actions: {
      setSearchField,
      setCompanyFilter,
      setBranchFilter,
      setSearchText,
      resetFilters,
      handleDelete,
      handleSearch: loadPages,
      refresh: loadPages,
    },
  }
}
